import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs, SmartOfferActions } from "@expert/shared-types";
import type { JSX } from "react";

import type { ExpertAssistMessage } from "../../shared-types";

import { getLastMessageOfType } from "../../state";
import { botMessageTypes, isFromExpert } from "../../utils";
import { TimelineItem } from "../timeline-item";
import { generateTimelineItemFromType } from "./generateTimelineItemForType";

interface GenerateTimelineItemProps {
    messages: ExpertAssistMessage[];
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
    scrollRef?: React.RefObject<HTMLDivElement | null>;
    smartOfferActions?: SmartOfferActions;
    isSaleSuccessOrDeclined?: boolean;
}

export function generateTimelineItems({
    messages = [],
    scrollRef,
    logger,
    gaiaPayloadProps,
    smartOfferActions,
    isSaleSuccessOrDeclined = false,
}: GenerateTimelineItemProps) {
    const items: JSX.Element[] = [];

    const lastBotMessage = getLastMessageOfType(messages, botMessageTypes);
    const activeMessages = messages.filter((msg) => msg.status === "active");
    const lastProactiveMessage = getLastMessageOfType(activeMessages, ["Proactive", "Default", "SessionSummary"]);

    let loadingBotMessageItem = null;
    if (messages.length) {
        const mostRecentMessage = messages[messages.length - 1];
        const isLoadingBotMessage = isFromExpert(mostRecentMessage.type) && !mostRecentMessage.metadata?.retryPayload;
        const showIdleBotIcon = isFromExpert(mostRecentMessage.type) && !!mostRecentMessage.metadata?.retryPayload;
        if (isLoadingBotMessage || showIdleBotIcon) {
            loadingBotMessageItem = (
                <TimelineItem
                    isLastBotMessage
                    isLastProactiveMessage
                    isLoading={isLoadingBotMessage}
                    key="LoadingBotMessage"
                    type="Proactive"
                    logger={logger}
                    gaiaPayloadProps={gaiaPayloadProps}
                />
            );
        }
    }

    items.push(
        ...messages.map((msg) => (
            <TimelineItem
                isLastBotMessage={!loadingBotMessageItem && msg.id === lastBotMessage}
                isLastProactiveMessage={msg.id === lastProactiveMessage}
                message={msg}
                type={msg.type}
                isLoading={false}
                key={`timeline-item-${msg.id}-${msg.type}`}
                scrollRef={scrollRef}
                metadata={msg.metadata}
                logger={logger}
                gaiaPayloadProps={gaiaPayloadProps}
                smartOfferActions={smartOfferActions}
                isSaleSuccessOrDeclined={isSaleSuccessOrDeclined}
            >
                {generateTimelineItemFromType(msg)}
            </TimelineItem>
        )),
    );

    if (loadingBotMessageItem) items.push(loadingBotMessageItem);

    return items;
}
